'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Button, Badge, Alert, Spinner, Form } from 'react-bootstrap';
import { Save, RotateCcw, FileText, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { ConfigReader } from '../../config/ConfigReader';
import { CsAgentClient } from '../../infrastructure/CsAgentClient';
import {
  CHATBOT_TYPE_LABELS,
  DEFAULT_CHATBOT_TYPE,
  VISIBLE_CHATBOT_TYPES,
  type ChatbotType,
} from '../../model/ChatbotType';

interface PromptToolItem {
  name: string;
  description: string;
}

const PROMPT_TOOLS_BY_SERVICE_TYPE: Record<ChatbotType, PromptToolItem[]> = {
  skinseoul: [
    { name: 'get_order', description: 'Look up order by ID' },
    { name: 'track_order_shipping', description: 'Look up tracking and delivery progress' },
    { name: 'verify_order_email', description: 'Verify anonymous order ownership' },
    { name: 'search_products', description: 'Search product catalog' },
    { name: 'search_knowledge_base', description: 'Search FAQ/policies' },
    { name: 'get_customer_info', description: 'Look up customer profile' },
    { name: 'get_shipping_info', description: 'Look up shipping policy by country' },
    { name: 'propose_action', description: 'Propose refund, escalation, etc.' },
    { name: 'resolve_proposed_action', description: 'Resolve a pending human-approved action' },
    { name: 'gorgias_add_internal_note', description: 'Add internal note' },
    { name: 'gorgias_tag_ticket', description: 'Tag the ticket' },
  ],
  creatorhub: [
    { name: 'search_knowledge_base', description: 'Search CreatorHub FAQ, campaign, and creator policy docs' },
    { name: 'gorgias_add_internal_note', description: 'Add factual handoff notes for the team' },
    { name: 'gorgias_tag_ticket', description: 'Tag CreatorHub tickets for routing and triage' },
  ],
};

export default function CsPromptEditorPage() {
  const [prompt, setPrompt] = useState('');
  const [savedPrompt, setSavedPrompt] = useState('');
  const [version, setVersion] = useState(0);
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [serviceType, setServiceType] = useState<ChatbotType>(DEFAULT_CHATBOT_TYPE);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const clientRef = useRef<CsAgentClient | null>(null);
  const serviceTypeRef = useRef<ChatbotType>(DEFAULT_CHATBOT_TYPE);

  const getClient = useCallback(() => {
    if (!clientRef.current) {
      const config = ConfigReader.getInstance().getConfig();
      clientRef.current = new CsAgentClient(config.apiUrl);
    }
    return clientRef.current;
  }, []);

  useEffect(() => {
    let isCurrentRequest = true;
    const requestedServiceType = serviceType;
    serviceTypeRef.current = requestedServiceType;

    const loadPrompt = async () => {
      setLoading(true);
      setError(null);
      try {
        const client = getClient();
        const result = await client.getPrompt(requestedServiceType);
        if (!isCurrentRequest || serviceTypeRef.current !== requestedServiceType) {
          return;
        }
        setPrompt(result.prompt);
        setSavedPrompt(result.prompt);
        setVersion(result.version);
        setIsDefault(result.isDefault);
      } catch (err) {
        if (!isCurrentRequest || serviceTypeRef.current !== requestedServiceType) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load prompt');
      } finally {
        if (isCurrentRequest && serviceTypeRef.current === requestedServiceType) {
          setLoading(false);
        }
      }
    };

    loadPrompt();
    return () => {
      isCurrentRequest = false;
    };
  }, [getClient, serviceType]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const client = getClient();
      const result = await client.savePrompt(prompt, serviceType);
      setVersion(result.version);
      setSavedPrompt(prompt);
      setIsDefault(false);
      setSuccess('System prompt saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Reset to default system prompt? This will overwrite your custom prompt.')) {
      return;
    }
    setResetting(true);
    setError(null);
    setSuccess(null);
    try {
      const client = getClient();
      const result = await client.resetPrompt(serviceType);
      setPrompt(result.prompt);
      setSavedPrompt(result.prompt);
      setVersion(result.version);
      setIsDefault(true);
      setSuccess('System prompt reset to default');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset prompt');
    } finally {
      setResetting(false);
    }
  };

  const hasChanges = prompt !== savedPrompt;
  const handleServiceTypeChange = (nextServiceType: ChatbotType) => {
    if (nextServiceType === serviceType) {
      return;
    }
    if (hasChanges && !confirm('Discard unsaved prompt changes?')) {
      return;
    }
    serviceTypeRef.current = nextServiceType;
    setServiceType(nextServiceType);
  };
  const charCount = prompt.length;
  const lineCount = prompt.split('\n').length;
  const availableTools = PROMPT_TOOLS_BY_SERVICE_TYPE[serviceType];

  if (loading) {
    return (
      <Container fluid className="py-4 d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid className="py-3 px-3" style={{ height: 'calc(100vh - 56px)' }}>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <FileText size={24} />
          <h5 className="mb-0">CS Agent System Prompt</h5>
          {isDefault ? <Badge bg="secondary">Default</Badge> : <Badge bg="primary">Custom</Badge>}
          {hasChanges && (
            <Badge bg="warning" text="dark">
              Unsaved Changes
            </Badge>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          <Form.Select
            size="sm"
            aria-label="Service type"
            value={serviceType}
            onChange={(event) => handleServiceTypeChange(event.target.value as ChatbotType)}
            style={{ width: 160 }}
          >
            {VISIBLE_CHATBOT_TYPES.map((type) => (
              <option key={type} value={type}>
                {CHATBOT_TYPE_LABELS[type]}
              </option>
            ))}
          </Form.Select>
          <Button variant="outline-secondary" size="sm" onClick={handleReset} disabled={resetting || saving}>
            {resetting ? <Spinner animation="border" size="sm" /> : <RotateCcw size={14} className="me-1" />}
            Reset to Default
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? <Spinner animation="border" size="sm" /> : <Save size={14} className="me-1" />}
            Save
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          <AlertTriangle size={14} className="me-1" />
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-3">
          <CheckCircle size={14} className="me-1" />
          {success}
        </Alert>
      )}

      <Row style={{ height: 'calc(100% - 80px)' }}>
        {/* Editor */}
        <Col md={8} className="h-100">
          <Card className="h-100 d-flex flex-column">
            <Card.Header className="d-flex align-items-center justify-content-between py-2">
              <span className="small fw-medium">Prompt Editor</span>
              <div className="d-flex align-items-center gap-3 small text-muted">
                <span>{lineCount} lines</span>
                <span>{charCount} chars</span>
                {version > 0 && (
                  <span className="d-flex align-items-center gap-1">
                    <Clock size={12} />v{version}
                  </span>
                )}
              </div>
            </Card.Header>
            <Card.Body className="p-0 flex-grow-1" style={{ minHeight: 0 }}>
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="w-100 h-100 border-0 p-3"
                style={{
                  resize: 'none',
                  fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontSize: '0.85rem',
                  lineHeight: 1.6,
                  outline: 'none',
                  backgroundColor: '#1e1e1e',
                  color: '#d4d4d4',
                }}
                spellCheck={false}
                autoFocus
              />
            </Card.Body>
          </Card>
        </Col>

        {/* Guide */}
        <Col md={4} className="h-100" style={{ overflowY: 'auto' }}>
          <Card className="mb-3">
            <Card.Header className="py-2">
              <span className="small fw-medium">Prompt Guide</span>
            </Card.Header>
            <Card.Body className="small">
              <p className="mb-2">
                The system prompt defines the CS Agent&apos;s personality, behavior, and knowledge. Changes apply to{' '}
                <strong>all new conversations</strong> within 5 minutes.
              </p>
              <h6 className="mt-3 mb-2" style={{ fontSize: '0.8rem' }}>
                Recommended Sections
              </h6>
              <ul className="mb-2" style={{ fontSize: '0.8rem', paddingLeft: '1.2rem' }}>
                <li>
                  <strong>Role</strong> — What the agent is and does
                </li>
                <li>
                  <strong>Tone</strong> — Warm, professional, casual, etc.
                </li>
                <li>
                  <strong>Language</strong> — Response language rules
                </li>
                <li>
                  <strong>Guidelines</strong> — How to handle orders, products, returns
                </li>
                <li>
                  <strong>Sensitive Actions</strong> — What needs human approval
                </li>
                <li>
                  <strong>What NOT to do</strong> — Guardrails and boundaries
                </li>
                <li>
                  <strong>Common Topics</strong> — Shipping, returns, payment info
                </li>
              </ul>
              <h6 className="mt-3 mb-2" style={{ fontSize: '0.8rem' }}>
                Tips
              </h6>
              <ul className="mb-0" style={{ fontSize: '0.8rem', paddingLeft: '1.2rem' }}>
                <li>
                  Use <code>##</code> headers to organize sections
                </li>
                <li>Be specific — vague instructions produce vague responses</li>
                <li>Test changes in CS Playground before going live</li>
                <li>Use &quot;Reset to Default&quot; to restore the original prompt</li>
              </ul>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header className="py-2">
              <span className="small fw-medium">Available Tools</span>
            </Card.Header>
            <Card.Body className="small">
              <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                The agent can use these tools. Reference them in your prompt to guide when they should be used.
              </p>
              <ul className="mb-0" style={{ fontSize: '0.8rem', paddingLeft: '1.2rem' }}>
                {availableTools.map((tool) => (
                  <li key={tool.name}>
                    <code>{tool.name}</code> — {tool.description}
                  </li>
                ))}
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
